/**
 * LabelPreview — the visual 80mm thermal docket preview rendered from the
 * memoised `label` payload. Print-only CSS lives in App.css/Index.css; this
 * component is the structural shell so the form file stays focused.
 */
import { QRCodeSVG } from "qrcode.react";
import { allergenLabel } from "@/lib/allergens";

export default function LabelPreview({ label, realSize = false, widthMm, heightMm }) {
  // Prefer the public verify-URL QR (1-tap audit-trail from a phone
  // camera). Fall back to the raw GS1 element string if we don't yet
  // have a verify URL — e.g. before the form has any identifying info.
  const qrValue = label.verifyUrl || label.gs1Payload || "";
  // Real-size preview uses CSS `mm` units so the on-screen render
  // matches the printed roll — managers can eyeball a 60×40 sticker
  // before wasting a batch of tape on a mis-sized run.
  const cardStyle = realSize
    ? {
        width: `${widthMm || 80}mm`,
        // Only cap height on-screen for the size preview. At print
        // time the height cap is lifted (see @media print rule in
        // index.css) so overflowing content flows onto a second /
        // third sticker instead of being clipped. Long product names
        // + many allergens used to lose the notes + QR footer.
        ...(heightMm ? { minHeight: `${heightMm}mm` } : {}),
      }
    : undefined;
  return (
    <div className="flex justify-center" data-print-scope="label">
      <div
        className={`label-card ${realSize ? "border-2 border-dashed border-eucalyptus-700/40" : ""}`}
        style={cardStyle}
        data-testid="label-card"
      >
        <div className="font-mono text-[11px] tracking-wider uppercase text-center border-b border-black pb-1">
          {label.site || "HACCP AU"}
        </div>
        <div className="mt-2 text-center">
          <div className="font-display font-extrabold text-lg leading-tight">
            {label.product}
          </div>
          {label.quantity && (
            <div className="text-[11px]">{label.quantity}</div>
          )}
        </div>
        <div className="mt-3 font-mono text-[11px] leading-snug">
          <div><b>PREP:</b> {label.prep}</div>
          <div className="text-base"><b>USE-BY:</b> {label.useby}</div>
          <div><b>BATCH:</b> {label.batch}</div>
          <div><b>STORE:</b> {label.storage}</div>
          <div><b>BY:</b> {label.staff}</div>
        </div>
        {label.allergens?.length > 0 && (
          <div
            className="mt-2 border-t border-black pt-1 font-mono text-[10px]"
            style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
          >
            <div className="font-bold">CONTAINS:</div>
            <div className="uppercase">
              {label.allergens.map(allergenLabel).join(", ")}
            </div>
          </div>
        )}
        {label.notes && (
          <div
            className="mt-2 font-mono text-[10px] border-t border-dashed border-black pt-1"
            style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
          >
            {label.notes}
          </div>
        )}
        {qrValue && (
          <div
            className="mt-3 flex flex-col items-center"
            data-testid="label-qr"
            style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
          >
            <QRCodeSVG
              value={qrValue}
              size={120}
              level="Q"
              marginSize={2}
              aria-label="Verify-label QR code"
            />
            <div className="mt-1 font-mono text-[9px] text-neutral-600">
              Scan to view audit trail
            </div>
          </div>
        )}
        <div className="mt-3 text-center font-mono text-[9px] text-neutral-600">
          Printed {new Date().toLocaleString("en-AU")}
        </div>
      </div>
    </div>
  );
}
